import React, { useState, useEffect, useRef } from 'react';
import { FiCalendar, FiUsers, FiPlus, FiTrash2, FiSearch, FiChevronDown, FiX, FiClock, FiBook, FiInfo, FiLayers, FiEdit2 } from 'react-icons/fi';
import api from '../../utils/api';
import { parseLocalDate, formatIndianDate, formatIndianDateNumeric } from '../../utils/dateUtils';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444' };

const SearchableDropdown = ({ label, placeholder, searchPlaceholder, items, value, displayValue, onSelect, renderItem, filterFn }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = items.filter(item => filterFn(item, query));

    return (
        <div style={{ position: 'relative' }} ref={ref}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 4 }}>{label}</label>
            <div
                onClick={() => setOpen(!open)}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: font, background: '#fff', cursor: 'pointer', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box' }}
            >
                {value ? (
                    <span style={{ color: C.text }}>{displayValue}</span>
                ) : (
                    <span style={{ color: '#9ca3af' }}>{placeholder}</span>
                )}
                <FiChevronDown size={14} color="#9ca3af" />
            </div>

            {open && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 250, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 8, borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            type="text"
                            placeholder={searchPlaceholder}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ width: '100%', padding: '6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 13, fontFamily: font, boxSizing: 'border-box', outline: 'none' }}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div style={{ overflowY: 'auto', maxHeight: 200 }}>
                        {filtered.length > 0 ? (
                            filtered.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => { onSelect(item); setOpen(false); setQuery(''); }}
                                    style={{ padding: '10px 12px', fontSize: 14, cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: 'transparent', color: C.text }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdf4'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    {renderItem(item)}
                                </div>
                            ))
                        ) : (
                            <div style={{ padding: 12, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Helper to parse any time string → "HH:MM" for <input type="time"> ──────────
// Handles: "10:10 AM", "10:10 A.M.", "2:10 PM", "2:10 P.M.", "14:10", "02:10"
const parseTimeTo24 = (timeStr) => {
    if (!timeStr || !timeStr.trim()) return '';
    const t = timeStr.trim();
    // Already in 24h format (HH:MM or H:MM)
    if (/^\d{1,2}:\d{2}$/.test(t)) {
        const [h, m] = t.split(':').map(Number);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    try {
        // Remove dots from A.M. / P.M. → AM / PM
        const cleaned = t.replace(/\./g, '').toUpperCase(); // "10:10 AM" or "2:10 PM"
        const match = cleaned.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/);
        if (!match) return '';
        let h = parseInt(match[1], 10);
        const m = match[2];
        const ap = match[3];
        if (ap === 'PM' && h !== 12) h += 12;
        if (ap === 'AM' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${m}`;
    } catch { return ''; }
};

// ─── Helper to format time for display ────────────────────────────────────────
const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setHours(h); d.setMinutes(m);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const EMPTY_SUBJECT = { subject_code: '', subject_name: '', exam_date: '', start_time: '', end_time: '' };

const AdminExams = () => {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);         // true = editing existing
    const [editingExam, setEditingExam] = useState(null);    // the group being edited
    const [selectedExam, setSelectedExam] = useState(null);  // for detail view

    const [departments, setDepartments] = useState([]);
    const [batches, setBatches] = useState([]);
    const [allSubjects, setAllSubjects] = useState([]);

    const [formData, setFormData] = useState({ title: '', department: '', batch: '', year: '2001' });
    const [selectedSubjects, setSelectedSubjects] = useState([{ ...EMPTY_SUBJECT }]);
    const [submitting, setSubmitting] = useState(false);
    const [formMsg, setFormMsg] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => { fetchData(); fetchFormData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/admin/exam-schedules');
            setExams(res.data.schedules || []);
        } catch (err) {
            console.error(err);
            setExams([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchFormData = async () => {
        try {
            const [deptRes, batchRes] = await Promise.all([
                api.get('/api/admin/departments'),
                api.get('/api/admin/batches'),
            ]);
            setDepartments(deptRes.data.departments || []);
            setBatches(batchRes.data.batches || []);
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        if (!formData.department) { setAllSubjects([]); return; }
        const params = new URLSearchParams({ department: formData.department });
        if (formData.batch) params.append('batch', formData.batch);
        api.get(`/api/admin/subjects?${params.toString()}`)
            .then(r => setAllSubjects(r.data.subjects || []))
            .catch(() => setAllSubjects([]));
    }, [formData.department, formData.batch]);

    const openCreateModal = () => {
        setEditMode(false);
        setEditingExam(null);
        setFormData({ title: '', department: '', batch: '', year: '2001' });
        setSelectedSubjects([{ ...EMPTY_SUBJECT }]);
        setFormMsg('');
        setShowModal(true);
    };

    const openEditModal = (exam) => {
        setEditMode(true);
        setEditingExam(exam);
        setFormData({
            title: exam.title || '',
            department: exam.department || '',
            batch: exam.batch || '',
            year: exam.year || '2001',
        });
        // Pre-populate subjects from existing exam data
        const populated = (exam.subjects || []).map(s => {
            // Split exam_time on " - " or " – " or just "-" (handles all DB formats)
            const timeStr = s.exam_time || '';
            // Match two time tokens separated by any dash/hyphen with optional spaces
            const timeMatch = timeStr.match(/^(.+?)\s*[-–]\s*(.+)$/);
            const startRaw = timeMatch ? timeMatch[1].trim() : '';
            const endRaw   = timeMatch ? timeMatch[2].trim() : '';
            return {
                subject_code: s.subject_code || '',
                subject_name: s.subject_name || '',
                exam_date: s.exam_date ? s.exam_date.slice(0, 10) : '',
                start_time: parseTimeTo24(startRaw),
                end_time: parseTimeTo24(endRaw),
            };
        });
        setSelectedSubjects(populated.length > 0 ? populated : [{ ...EMPTY_SUBJECT }]);
        setFormMsg('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditMode(false);
        setEditingExam(null);
        setFormMsg('');
    };

    const handleAddSubjectRow = () => setSelectedSubjects([...selectedSubjects, { ...EMPTY_SUBJECT }]);
    const handleRemoveSubjectRow = (index) => { const n = [...selectedSubjects]; n.splice(index, 1); setSelectedSubjects(n); };
    const handleSubjectChange = (index, field, value) => { const n = [...selectedSubjects]; n[index][field] = value; setSelectedSubjects(n); };
    const handleSelectExistingSubject = (index, sub) => { const n = [...selectedSubjects]; n[index].subject_code = sub.subject_code; n[index].subject_name = sub.subject_name; setSelectedSubjects(n); };

    const buildValidSubjects = () => selectedSubjects
        .filter(s => s.subject_code && s.subject_name && s.exam_date && s.start_time && s.end_time)
        .map(s => ({ ...s, exam_time: `${formatTime(s.start_time)} - ${formatTime(s.end_time)}` }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.department || !formData.year) { alert('Please fill all required fields.'); return; }
        const validSubjects = buildValidSubjects();
        if (validSubjects.length === 0) { alert('Please fill out at least one subject completely.'); return; }

        setSubmitting(true);
        setFormMsg('');
        try {
            if (editMode && editingExam) {
                await api.put('/api/admin/exam-schedules/group', {
                    originalDepartment: editingExam.department,
                    originalYear: editingExam.year,
                    originalBatch: editingExam.batch || null,
                    department: formData.department,
                    year: formData.year,
                    batch: formData.batch || null,
                    subjects: validSubjects,
                });
            } else {
                await api.post('/api/admin/exam-schedules', {
                    ...formData,
                    subjects: validSubjects,
                });
            }
            closeModal();
            fetchData();
        } catch (err) {
            setFormMsg(err.response?.data?.message || (editMode ? 'Error updating schedule' : 'Error creating schedule'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteGroup = async (exam) => {
        if (!window.confirm(`Delete ALL subjects for "${exam.title}"? This cannot be undone.`)) return;
        try {
            await api.delete('/api/admin/exam-schedules/group/delete', {
                data: { department: exam.department, year: exam.year, batch: exam.batch || null }
            });
            fetchData();
        } catch {
            alert('Error deleting schedule');
        }
    };

    const getStatusStyle = (status) => {
        if (status === 'upcoming') return { bg: '#e0e7ff', color: '#4f46e5' };
        if (status === 'draft') return { bg: '#fffbeb', color: C.orange };
        return { bg: '#ecfdf5', color: C.primary };
    };

    const getDynamicStatus = (examRow) => {
        if (!examRow.subjects || examRow.subjects.length === 0) return 'draft';
        let latestDate = new Date(0);
        examRow.subjects.forEach(s => { if (s.exam_date) { const d = parseLocalDate(s.exam_date); if (d && d > latestDate) latestDate = d; } });
        return new Date() > latestDate ? 'completed' : 'upcoming';
    };

    const yearLabels = { '2001': '1st Year', '2002': '2nd Year', '2003': '3rd Year', '2004': '4th Year' };

    const calcDuration = (timeStr) => {
        if (!timeStr) return '—';
        const parts = timeStr.split(' - ');
        if (parts.length !== 2) return timeStr;
        try {
            const parseT = (t) => {
                const [time, ap] = t.trim().split(' ');
                let [h, m] = time.split(':').map(Number);
                if (ap?.toUpperCase() === 'PM' && h !== 12) h += 12;
                if (ap?.toUpperCase() === 'AM' && h === 12) h = 0;
                return h * 60 + m;
            };
            const diff = parseT(parts[1]) - parseT(parts[0]);
            if (diff <= 0) return timeStr;
            const hrs = Math.floor(diff / 60);
            const mins = diff % 60;
            return hrs > 0 ? `${hrs}h ${mins > 0 ? mins + 'm' : ''}` : `${mins}m`;
        } catch { return timeStr; }
    };

    return (
        <div style={{ padding: '0px 10px', fontFamily: font, maxWidth: 1200, margin: '0 auto', animation: 'fadeIn 0.4s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiCalendar color={C.primary} /> Exam Schedules
                    </h1>
                    <p style={{ margin: 0, color: C.sub, fontSize: 14 }}>Manage and schedule upcoming examinations timetables.</p>
                </div>
                <button onClick={openCreateModal} style={{ padding: '10px 20px', borderRadius: 8, background: C.primary, color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontFamily: font, boxShadow: '0 4px 12px rgba(16,185,129,0.2)' }}>
                    <FiPlus size={18} /> Schedule Exam
                </button>
            </div>

            {/* ── Search bar ─────────────────────────────────────── */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, padding: '10px 16px', maxWidth: 460, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <FiSearch size={16} color={C.sub} style={{ flexShrink: 0 }} />
                    <input
                        type="text"
                        placeholder="Search by department, batch, year, subject code..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ border: 'none', outline: 'none', fontSize: 14, fontFamily: font, flex: 1, color: C.text, background: 'transparent' }}
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                    )}
                </div>
                {searchQuery && (
                    <div style={{ marginTop: 6, fontSize: 12, color: C.sub }}>
                        {exams.filter(e => {
                            const q = searchQuery.toLowerCase();
                            return (e.department||'').toLowerCase().includes(q) ||
                                (e.batch||'').toLowerCase().includes(q) ||
                                (e.title||'').toLowerCase().includes(q) ||
                                (e.subjects||[]).some(s => (s.subject_code||'').toLowerCase().includes(q) || (s.subject_name||'').toLowerCase().includes(q));
                        }).length} result{exams.filter(e => {
                            const q = searchQuery.toLowerCase();
                            return (e.department||'').toLowerCase().includes(q)||(e.batch||'').toLowerCase().includes(q)||(e.title||'').toLowerCase().includes(q)||(e.subjects||[]).some(s=>(s.subject_code||'').toLowerCase().includes(q)||(s.subject_name||'').toLowerCase().includes(q));
                        }).length !== 1 ? 's' : ''} found
                    </div>
                )}
            </div>

            {loading ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 }}>
                    {[...Array(6)].map((_, i) => (
                        <div key={i} style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, padding: 24, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                                <div style={{ width: 80, height: 20, borderRadius: 6, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                                <div style={{ width: 60, height: 28, borderRadius: 6, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                            </div>
                            <div style={{ width: '80%', height: 18, borderRadius: 6, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: 8 }} />
                            <div style={{ width: '55%', height: 14, borderRadius: 6, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: 16 }} />
                            {[...Array(4)].map((_, j) => (
                                <div key={j} style={{ width: '100%', height: 12, borderRadius: 4, background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: 8 }} />
                            ))}
                        </div>
                    ))}
                    <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
                </div>
            ) : exams.filter(e => {
                const q = searchQuery.toLowerCase();
                return !q ||
                    (e.department||'').toLowerCase().includes(q) ||
                    (e.batch||'').toLowerCase().includes(q) ||
                    (e.title||'').toLowerCase().includes(q) ||
                    (e.subjects||[]).some(s => (s.subject_code||'').toLowerCase().includes(q) || (s.subject_name||'').toLowerCase().includes(q));
            }).length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: C.sub, background: '#fff', borderRadius: 16, border: `1px solid ${C.border}` }}>
                    {searchQuery ? `No exam schedules match "${searchQuery}". Try a different search term.` : 'No exam schedules found. Click "Schedule Exam" to create one.'}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 }}>
                    {exams.filter(e => {
                        const q = searchQuery.toLowerCase();
                        return !q ||
                            (e.department||'').toLowerCase().includes(q) ||
                            (e.batch||'').toLowerCase().includes(q) ||
                            (e.title||'').toLowerCase().includes(q) ||
                            (e.subjects||[]).some(s => (s.subject_code||'').toLowerCase().includes(q) || (s.subject_name||'').toLowerCase().includes(q));
                    }).map((e) => {
                        const dynStatus = getDynamicStatus(e);
                        const statusStyle = getStatusStyle(dynStatus);
                        return (
                            <div key={`${e.department}_${e.year}_${e.batch}`} onClick={() => setSelectedExam(e)} style={{
                                background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`,
                                padding: 24, boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                display: 'flex', flexDirection: 'column', gap: 12, position: 'relative', overflow: 'hidden',
                                cursor: 'pointer', transition: 'box-shadow 0.2s, transform 0.2s'
                            }}
                                onMouseEnter={ev => { ev.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; ev.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={ev => { ev.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.03)'; ev.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: statusStyle.color }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ padding: '4px 10px', borderRadius: 6, background: statusStyle.bg, color: statusStyle.color, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {dynStatus}
                                    </div>
                                    {/* ─── Edit & Delete Buttons ─── */}
                                    <div style={{ display: 'flex', gap: 6 }} onClick={ev => ev.stopPropagation()}>
                                        <button
                                            onClick={() => openEditModal(e)}
                                            title="Edit schedule"
                                            style={{ background: '#e0f2fe', border: 'none', color: '#0369a1', cursor: 'pointer', padding: '5px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, fontFamily: font }}
                                        >
                                            <FiEdit2 size={13} /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteGroup(e)}
                                            title="Delete schedule"
                                            style={{ background: '#fee2e2', border: 'none', color: C.red, cursor: 'pointer', padding: '5px 8px', borderRadius: 6, display: 'flex', alignItems: 'center' }}
                                        >
                                            <FiTrash2 size={14} />
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: C.text, lineHeight: 1.3 }}>{e.title}</h3>
                                    <p style={{ margin: 0, fontSize: 13, color: C.sub }}>{e.department}{e.batch ? ` - ${e.batch}` : ''}</p>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: C.sub, background: C.bg, padding: 12, borderRadius: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.border}`, paddingBottom: 8, marginBottom: 4 }}>
                                        <FiUsers color={C.primary} size={16} /> <span style={{ fontWeight: 500 }}>{e.enrolled_students} Enrolled Students</span>
                                    </div>
                                    <div style={{ fontWeight: 700, color: C.text, marginBottom: 2 }}>{e.subjects?.length || 0} Subjects Scheduled:</div>
                                    <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {e.subjects && e.subjects.map((s, idx) => (
                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                                <span style={{ fontWeight: 600, color: C.text, maxWidth: '60%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.subject_name}>{s.subject_code}</span>
                                                <div style={{ display: 'flex', gap: 6, color: C.sub }}>
                                                    {s.exam_date && <span>{formatIndianDateNumeric(s.exam_date)}</span>}
                                                    {s.exam_time && <span>{s.exam_time}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ─── Create / Edit Modal ─────────────────────────────────────────── */}
            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', animation: 'slideUp 0.3s ease' }}>

                        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {editMode && <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800 }}>EDITING</span>}
                                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text }}>{editMode ? 'Edit Exam Schedule' : 'Create Exam Timetable'}</h2>
                            </div>
                            <button onClick={closeModal} style={{ background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.sub }}>
                                <FiX size={18} />
                            </button>
                        </div>

                        <div style={{ padding: 24, overflowY: 'auto' }}>
                            {formMsg && <div style={{ padding: '10px 14px', background: '#fee2e2', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600, color: C.red }}>{formMsg}</div>}
                            <form id="examForm" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    {/* 1. Department */}
                                    <div>
                                        <SearchableDropdown
                                            label="Department *"
                                            placeholder="-- Select Department --"
                                            searchPlaceholder="Search dept..."
                                            items={departments}
                                            value={formData.department}
                                            displayValue={formData.department}
                                            onSelect={(dept) => setFormData({ ...formData, department: dept, batch: '', year: '2001' })}
                                            filterFn={(dept, q) => (dept || '').toLowerCase().includes(q.toLowerCase())}
                                            renderItem={(dept) => <div style={{ fontWeight: 500 }}>{dept}</div>}
                                        />
                                    </div>

                                    {/* 2. Year */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 4 }}>Year *</label>
                                        <select value={formData.year} onChange={e => setFormData({ ...formData, year: e.target.value, batch: '' })} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: font, boxSizing: 'border-box', background: '#fff' }}>
                                            <option value="2001">1st Year</option>
                                            <option value="2002">2nd Year</option>
                                            <option value="2003">3rd Year</option>
                                            <option value="2004">4th Year</option>
                                        </select>
                                    </div>

                                    {/* 3. Batch */}
                                    <div style={{ gridColumn: 'span 2' }}>
                                        <SearchableDropdown
                                            label="Batch"
                                            placeholder={formData.department ? '-- Select Batch --' : 'Select department first'}
                                            searchPlaceholder="Search batch..."
                                            items={formData.department ? batches.filter(b => b.department && b.department.trim().toLowerCase() === formData.department.trim().toLowerCase() && String(b.year) === formData.year) : []}
                                            value={formData.batch}
                                            displayValue={formData.batch}
                                            onSelect={(b) => setFormData({ ...formData, batch: b.batch_name })}
                                            filterFn={(b, q) => (b.batch_name || '').toLowerCase().includes(q.toLowerCase())}
                                            renderItem={(b) => <div style={{ fontWeight: 500 }}>{b.batch_name}</div>}
                                        />
                                    </div>
                                </div>

                                <div style={{ height: 1, background: C.border }} />

                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text }}>Subjects Schedule</h3>
                                        <button type="button" onClick={handleAddSubjectRow} style={{ background: '#ecfdf5', color: C.primary, border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                            <FiPlus size={14} /> Add Subject
                                        </button>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {selectedSubjects.map((sub, idx) => (
                                            <div key={idx} style={{ background: C.bg, padding: 16, borderRadius: 12, border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}>
                                                {selectedSubjects.length > 1 && (
                                                    <button type="button" onClick={() => handleRemoveSubjectRow(idx)} style={{ position: 'absolute', top: 12, right: 12, background: '#fee2e2', color: C.red, border: 'none', width: 24, height: 24, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                )}

                                                <div style={{ gridColumn: 'span 3' }}>
                                                    <SearchableDropdown
                                                        label={`Select Subject ${idx + 1}`}
                                                        placeholder="-- Search by subject name or code --"
                                                        searchPlaceholder="Search subject..."
                                                        items={allSubjects}
                                                        value={sub.subject_code}
                                                        displayValue={sub.subject_name ? `${sub.subject_code} - ${sub.subject_name}` : sub.subject_code}
                                                        onSelect={(s) => handleSelectExistingSubject(idx, s)}
                                                        filterFn={(s, q) => (s.subject_code || '').toLowerCase().includes(q.toLowerCase()) || (s.subject_name || '').toLowerCase().includes(q.toLowerCase())}
                                                        renderItem={(s) => (
                                                            <>
                                                                <div style={{ fontWeight: 500 }}>{s.subject_name}</div>
                                                                <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{s.subject_code}</div>
                                                            </>
                                                        )}
                                                    />
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 1.5fr', gap: 12 }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 4 }}>Date</label>
                                                        <div style={{ position: 'relative' }}>
                                                            <input 
                                                                required 
                                                                type="date" 
                                                                value={sub.exam_date} 
                                                                onChange={e => handleSubjectChange(idx, 'exam_date', e.target.value)} 
                                                                style={{ 
                                                                    width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, 
                                                                    fontSize: 14, fontFamily: font, boxSizing: 'border-box',
                                                                    color: sub.exam_date ? 'transparent' : '#9ca3af',
                                                                    cursor: 'pointer'
                                                                }} 
                                                            />
                                                            {sub.exam_date && (
                                                                <div style={{ 
                                                                    position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)', 
                                                                    fontSize: 14, color: C.text, pointerEvents: 'none', fontFamily: font 
                                                                }}>
                                                                    {formatIndianDateNumeric(sub.exam_date)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 4 }}>Start Time</label>
                                                            <input required type="time" value={sub.start_time} onChange={e => handleSubjectChange(idx, 'start_time', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: font, boxSizing: 'border-box' }} />
                                                        </div>
                                                        <div style={{ paddingBottom: 10, color: C.sub, fontWeight: 600 }}>to</div>
                                                        <div style={{ flex: 1 }}>
                                                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 4 }}>End Time</label>
                                                            <input required type="time" value={sub.end_time} onChange={e => handleSubjectChange(idx, 'end_time', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: font, boxSizing: 'border-box' }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, background: C.bg, display: 'flex', justifyContent: 'flex-end', gap: 12, borderRadius: '0 0 20px 20px' }}>
                            <button type="button" onClick={closeModal} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', color: C.sub, border: `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 600, fontFamily: font }}>
                                Cancel
                            </button>
                            <button type="submit" form="examForm" disabled={submitting} style={{ padding: '10px 24px', borderRadius: 8, background: editMode ? '#0ea5e9' : C.primary, color: '#fff', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 700, fontFamily: font, opacity: submitting ? 0.7 : 1 }}>
                                {submitting ? 'Saving...' : (editMode ? '✏️ Update Schedule' : 'Save Schedule')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Detail View Modal ───────────────────────────────────────────── */}
            {selectedExam && (() => {
                const exam = selectedExam;
                const dynStatus = getDynamicStatus(exam);
                const statusStyle = getStatusStyle(dynStatus);
                const totalSubjects = exam.subjects?.length || 0;

                return (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setSelectedExam(null)}>
                        <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', animation: 'slideUp 0.3s ease', overflow: 'hidden' }}>

                            {/* Header Banner */}
                            <div style={{ background: `linear-gradient(135deg, ${statusStyle.color}, ${statusStyle.color}dd)`, padding: '24px 28px', color: '#fff', position: 'relative' }}>
                                <button onClick={() => setSelectedExam(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
                                    <FiX size={18} />
                                </button>
                                <div style={{ padding: '3px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.2)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, display: 'inline-block', marginBottom: 10 }}>{dynStatus}</div>
                                <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 900 }}>{exam.title}</h2>
                                <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>{exam.department}{exam.batch ? ` — ${exam.batch}` : ''}</p>
                            </div>

                            {/* Body */}
                            <div style={{ padding: 24, overflowY: 'auto' }}>
                                {/* Quick Stats */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                                    {[
                                        { icon: <FiBook size={18} />, label: 'Subjects', val: totalSubjects, c: '#4f46e5', bg: '#e0e7ff' },
                                        { icon: <FiLayers size={18} />, label: 'Year', val: yearLabels[exam.year] || exam.year, c: '#059669', bg: '#d1fae5' },
                                        { icon: <FiCalendar size={18} />, label: 'Batch', val: exam.batch || '—', c: '#d97706', bg: '#fef3c7' },
                                        { icon: <FiUsers size={18} />, label: 'Students', val: exam.enrolled_students, c: '#0ea5e9', bg: '#e0f2fe' },
                                    ].map((s, i) => (
                                        <div key={i} style={{ textAlign: 'center', padding: '14px 10px', borderRadius: 14, background: s.bg }}>
                                            <div style={{ color: s.c, marginBottom: 6 }}>{s.icon}</div>
                                            <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{s.val}</div>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Exam Details */}
                                <div style={{ background: C.bg, borderRadius: 14, border: `1px solid ${C.border}`, padding: 16, marginBottom: 20 }}>
                                    <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 800, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}><FiInfo size={14} color={C.primary} /> Exam Details</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                                        <div><span style={{ color: C.sub, fontWeight: 600 }}>Department:</span> <strong>{exam.department}</strong></div>
                                        <div><span style={{ color: C.sub, fontWeight: 600 }}>Batch:</span> <strong>{exam.batch || '—'}</strong></div>
                                        <div><span style={{ color: C.sub, fontWeight: 600 }}>Year:</span> <strong>{yearLabels[exam.year] || exam.year}</strong></div>
                                        <div><span style={{ color: C.sub, fontWeight: 600 }}>Status:</span> <span style={{ padding: '2px 8px', borderRadius: 6, background: statusStyle.bg, color: statusStyle.color, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>{dynStatus}</span></div>
                                        <div style={{ gridColumn: 'span 2' }}><span style={{ color: C.sub, fontWeight: 600 }}>Enrolled:</span> <strong>{exam.enrolled_students} students</strong></div>
                                    </div>
                                </div>

                                {/* Subjects Table */}
                                <div style={{ background: C.bg, borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 14, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <FiBook size={14} color={C.primary} /> Subjects ({totalSubjects})
                                    </div>
                                    {!exam.subjects || exam.subjects.length === 0 ? (
                                        <div style={{ padding: 20, textAlign: 'center', color: C.sub, fontSize: 13 }}>No subjects scheduled.</div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                <thead>
                                                    <tr style={{ background: '#fff' }}>
                                                        {['#', 'Code', 'Subject Name', 'Date', 'Time', 'Duration'].map(h => (
                                                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {exam.subjects.map((s, idx) => (
                                                        <tr key={s.id || idx} style={{ borderBottom: `1px solid ${C.border}` }}
                                                            onMouseEnter={ev => ev.currentTarget.style.background = '#f0fdf4'}
                                                            onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                                                            <td style={{ padding: '10px 14px', color: C.sub, fontWeight: 600 }}>{idx + 1}</td>
                                                            <td style={{ padding: '10px 14px', fontWeight: 800, color: '#4f46e5' }}>{s.subject_code}</td>
                                                            <td style={{ padding: '10px 14px', fontWeight: 600, color: C.text }}>{s.subject_name}</td>
                                                            <td style={{ padding: '10px 14px', color: C.text }}>{s.exam_date ? formatIndianDateNumeric(s.exam_date) : '—'}</td>
                                                            <td style={{ padding: '10px 14px', color: C.text }}>{s.exam_time || '—'}</td>
                                                            <td style={{ padding: '10px 14px' }}>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 700 }}>
                                                                    <FiClock size={11} /> {calcDuration(s.exam_time)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, background: C.bg, display: 'flex', justifyContent: 'space-between', borderRadius: '0 0 20px 20px' }}>
                                <button onClick={() => { setSelectedExam(null); openEditModal(exam); }} style={{ padding: '10px 20px', borderRadius: 8, background: '#e0f2fe', color: '#0369a1', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: font, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiEdit2 size={14} /> Edit Schedule
                                </button>
                                <button onClick={() => setSelectedExam(null)} style={{ padding: '10px 24px', borderRadius: 8, background: C.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: font }}>Close</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}} />
        </div>
    );
};

export default AdminExams;
